import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {HorribleHeroesGameMode} from "../../HorribleHeroesGameMode";
import {HorribleHeroesParsedInitData} from "../../server/data/HorribleHeroesParsedInitData";
import {HorribleHeroesSoundConfig} from "../../HorribleHeroesSoundConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export class SoundManager {
    private static currentLoop: string = "";
    private _shouldPlayBonusBG: boolean = false;
    private _isBonus: boolean = false;
    private _isMayhemMode: boolean = false;

    constructor() {
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, () => this.onReady());
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    private static get soundPlayer(): INolimitAudioPlayer {
        return <INolimitAudioPlayer>SlotGame.sound;
    }

    public static playAmbientLoop(name: string, duration?: number, volume?: number): void {
        this.currentLoop = name;
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume: volume
        });
    }

    public static playSound(name: string, repeat?: number, volume?: number): void {
        this.soundPlayer.playTrack({
            name: name,
            repeat: repeat,
            volume: volume
        });
    }

    public static stopSound(name: string): void {
        this.soundPlayer.stopTrack(name);
    }

    public static playReelLanding(reelId: number): void {
        Logger.logDev("sound reelLanding", (reelId + 1));
        this.playSound(HorribleHeroesSoundConfig.instance.REELS_DEFAULT_BOUNCE[reelId]);
    }

    public static playReelsSpinning(): void {
        Logger.logDev("sound reelsSpinning");
        this.playSound(HorribleHeroesSoundConfig.instance.REELS_SPIN, Infinity);
    }

    public static playIntroSplashIn(): void {
        Logger.logDev("sound introSplashIn");
        this.playSound(HorribleHeroesSoundConfig.instance.INTRO_SPLASH_IN);
    }

    public static playIntroSplashOut(): void {
        Logger.logDev("sound introSplashOut");
        this.playSound(HorribleHeroesSoundConfig.instance.INTRO_SPLASH_OUT);
    }

    public static playBetlineSweep(idx: number): void {
        Logger.logDev("sound betlineSweep", (idx + 1));
        this.playSound(HorribleHeroesSoundConfig.instance.BETLINE_SWEEP[idx]);
    }

    public static playCountUp(): void {
        Logger.logDev("sound countup");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP, Infinity);
    }

    public static playCountUpFire(): void {
        Logger.logDev("sound countupFire");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_FIRE);
    }

    public static playCountUpEndsTiny(): void {
        Logger.logDev("sound countupEndsTiny");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_ENDS_TINY);
    }

    public static playCountUpEndsSmall(): void {
        Logger.logDev("sound countupEndsSmall");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_ENDS_SMALL);
    }

    public static playCountUpEndsMedium(): void {
        Logger.logDev("sound countupEndsMedium");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_ENDS_MEDIUM);
    }

    public static playCountUpEndsLarge(): void {
        Logger.logDev("sound countupEndsLarge");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_ENDS_LARGE);
    }

    public static playWildLanding(): void {
        Logger.logDev("sound wildLanding");
        this.playSound(HorribleHeroesSoundConfig.instance.WILD_LANDING);
    }

    public static playXWaysLanding(): void {
        Logger.logDev("sound xwaysLanding");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_LANDING);
    }

    public static playXWaysAnticipation(): void {
        Logger.logDev("sound xwaysAnticipation");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_ANTICIPATION);
    }

    public static playXWaysElectricity(): void {
        Logger.logDev("sound xwaysElectricity");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_ELECTRICITY);
    }

    public static playXWaysReveal(): void {
        Logger.logDev("sound xwaysReveal");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_REVEAL);
    }

    public static playXWaysExpandInfectious(): void {
        Logger.logDev("sound xwaysExpandInfectious");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_EXPAND_INFECTIOUS);
    }

    public static playXWaysRevealInfectious(): void {
        Logger.logDev("sound xwaysRevealInfectious");
        this.playSound(HorribleHeroesSoundConfig.instance.X_WAYS_REVEAL_INFECTIOUS);
    }

    public static playBigWinInitialWin(): void {
        Logger.logDev("sound bigwinInitialWin");
        this.playSound(HorribleHeroesSoundConfig.instance.BIG_WIN_INITIAL_WIN);
    }

    public static playBigWinLevel(level: number): void {
        Logger.logDev("sound bigwinLevel" + level);
        this.playSound(HorribleHeroesSoundConfig.instance.BIG_WIN_LEVEL + level);

        // #444
        if (level === 1 || level === 2) {
            this.soundPlayer.fadeTrack({
                name: this.currentLoop,
                to: 0,
                duration: 100
            });

            this.stopSound(HorribleHeroesSoundConfig.instance.BIG_WIN_INITIAL_WIN);
        }
    }

    public static playBigWinEnds(): void {
        Logger.logDev("sound bigwinEnds");
        this.playSound(HorribleHeroesSoundConfig.instance.BIG_WIN_ENDS);

        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 1,
            duration: 1500
        });
    }

    public static playBigWinEndsLevel5(): void {
        Logger.logDev("sound bigwinEndsLevel5");
        this.playSound(HorribleHeroesSoundConfig.instance.BIG_WIN_ENDS_LEVEL5);

        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 1,
            duration: 1500
        });
    }

    public static playScatter(count: number): void {
        Logger.logDev("sound scatter" + count);
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER + count);
    }

    public static playScatterFail(): void {
        Logger.logDev("sound scatterFail");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_FAIL);
    }

    public static playScatterAnticipation(): void {
        Logger.logDev("sound scatterAnticipation");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_ANTICIPATION);
    }

    public static playScatterExplode(): void {
        Logger.logDev("sound scatterExplode");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_EXPLODE);
    }

    public static playScatterAnticipationAlarm(): void {
        Logger.logDev("sound scatterAnticipationAlarm");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_ANTICIPATION_ALARM);
    }

    public static playScatterAnticipationFail(): void {
        Logger.logDev("sound scatterAnticipationFail");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_ANTICIPATION_FAIL);
    }

    public static playScatterAnticipationWin(): void {
        Logger.logDev("sound scatterAnticipationWin");
        this.playSound(HorribleHeroesSoundConfig.instance.SCATTER_ANTICIPATION_WIN);
    }

    public static playFSWinAttention(): void {
        Logger.logDev("sound fsWinAttention");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_WIN_ATTENTION);
    }

    public static playFSSelectFadein(): void {
        Logger.logDev("sound fsSelectFadein");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_SELECT_FADE_IN);
    }

    public static playFSSelect(): void {
        Logger.logDev("sound fsSelect");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_SELECT);
    }

    public static playFSStartDoorClose(): void {
        Logger.logDev("sound fsStartDoorClose");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_START_DOOR_CLOSE);
    }

    public static playFSStartDoorOpen(): void {
        Logger.logDev("sound fsStartDoorOpen");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_START_DOOR_OPEN);
    }

    public static playFreeSpinBG(): void {
        Logger.logDev("sound switching to background-music-fs");

        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.MAIN_GAME_AMBIANCE_1);

        this.currentLoop = HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS;
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 0,
            duration: 1
        });
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 1,
            duration: 1
        });
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume: 1
        });
    }

    public static playSuperFreeSpinBGFromMainGame(): void {
        Logger.logDev("sound switching to background-music-fs-super");

        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.MAIN_GAME_AMBIANCE_1);

        this.currentLoop = HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS_SUPER;
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 0,
            duration: 1
        });
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 1,
            duration: 1
        });
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume: 1
        });
    }

    public static playLightning(count: number): void {
        Logger.logDev("sound lightning" + count);
        this.playSound(HorribleHeroesSoundConfig.instance.LIGHTNING + count);
    }

    public static playToxicShuffle(): void {
        Logger.logDev("sound toxicShuffle");
        this.playSound(HorribleHeroesSoundConfig.instance.TOXIC_SHUFFLE);
    }

    public static playToxicWild(): void {
        Logger.logDev("sound toxicWild");
        this.playSound(HorribleHeroesSoundConfig.instance.TOXIC_WILD);
    }

    public static playToxicWild4(): void {
        Logger.logDev("sound toxicWild4");
        this.playSound(HorribleHeroesSoundConfig.instance.TOXIC_WILD_4);
    }

    public static playToxicWild5(): void {
        Logger.logDev("sound toxicWild5");
        this.playSound(HorribleHeroesSoundConfig.instance.TOXIC_WILD_5);
    }

    public static playWildLandingLock(): void {
        Logger.logDev("sound wildLandingLock");
        this.playSound(HorribleHeroesSoundConfig.instance.WILD_LANDING_LOCK);
    }

    public static playFSWildMultiplier(): void {
        Logger.logDev("sound fsWildMultiplier");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_WILD_MULTIPLIER);
    }

    public static playFSPlusSpin(): void {
        Logger.logDev("sound fsPlusSpin");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_PLUS_SPIN);
    }

    public static playFSMeterExplode(): void {
        Logger.logDev("sound fsMeterExplode");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_METER_EXPLODE);
    }

    public static playFSMayhemSplash(): void {
        Logger.logDev("sound fsMayhemSplash");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_MAYHEM_SPLASH);
    }

    public static playMXLanding(): void {
        Logger.logDev("sound mxLanding");
        this.playSound(HorribleHeroesSoundConfig.instance.MX_LANDING);
    }

    public static playSuperFreeSpinBGFromFS(): void {
        Logger.logDev("sound switching to background-music-fs-super");

        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS);

        this.currentLoop = HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS_SUPER;
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 0,
            duration: 1
        });
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 1,
            duration: 1
        });
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume: 1
        });
    }

    public static playCountupEndsFsOutro(): void {
        Logger.logDev("sound countupEndsFsOutro");
        this.playSound(HorribleHeroesSoundConfig.instance.COUNT_UP_ENDS_FS_OUTRO);
    }

    public static playFSEnds(): void {
        Logger.logDev("sound fsEnds");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_ENDS);
    }

    public static playFSEndsBad(): void {
        Logger.logDev("sound fsEndsBad");
        this.playSound(HorribleHeroesSoundConfig.instance.FS_ENDS_BAD);
    }

    public static playCapWinBetlineSweep(): void {
        Logger.logDev("sound capWinBetlineSweep");
        this.playSound(HorribleHeroesSoundConfig.instance.CAP_WIN_BETLINE_SWEEEP);

        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            to: 0,
            duration: 1550
        })
    }

    public static playCapWinFadeIn(): void {
        Logger.logDev("sound capWinFadeIn");
        this.playSound(HorribleHeroesSoundConfig.instance.CAP_WIN_FADE_IN);
    }

    public static playCapWinCountUp(): void {
        Logger.logDev("sound capWinCountup");
        this.playSound(HorribleHeroesSoundConfig.instance.CAP_WIN_COUNT_UP);
    }

    public static playCapWinCountUpEnds(): void {
        Logger.logDev("sound capWinCountupEnds");
        this.playSound(HorribleHeroesSoundConfig.instance.CAP_WIN_COUNT_UP_ENDS);
    }

    public static playBackgroundMusicCapWin(): void {
        Logger.logDev("sound background-music-cap-win");
        this.playSound(HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_CAP_WIN);
    }

    public static playCapWinEnds(): void {
        Logger.logDev("sound capWinEnds");
        this.playSound(HorribleHeroesSoundConfig.instance.CAP_WIN_ENDS);
    }

    public static fadeOutBonusBG(): void {
        Logger.logDev("sound switching to background-music-main");

        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS);
        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_FS_SUPER);
        this.soundPlayer.stopTrack(HorribleHeroesSoundConfig.instance.BACKGROUND_MUSIC_CAP_WIN);

        this.currentLoop = HorribleHeroesSoundConfig.instance.MAIN_GAME_AMBIANCE_1;
        this.soundPlayer.playTrack({
            name: this.currentLoop,
            volume: 1
        });
    }

    private onInitDataParsed(data: HorribleHeroesParsedInitData): void {
        this._isBonus = (data.mode === HorribleHeroesGameMode.FREESPIN_LOW || data.nextMode === HorribleHeroesGameMode.FREESPIN_MID);
        this._shouldPlayBonusBG = this._isBonus && (data.nextMode === HorribleHeroesGameMode.FREESPIN_LOW || data.nextMode === HorribleHeroesGameMode.FREESPIN_MID);
        this._isMayhemMode = this._shouldPlayBonusBG && (data.getExpandedSymbol === "MX");

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private onReady(): void {
        if (this._isBonus) {
            SoundManager.playAmbientLoop(HorribleHeroesSoundConfig.instance.MAIN_GAME_AMBIANCE_1, undefined, 0);
            if (this._isMayhemMode) {
                SoundManager.playSuperFreeSpinBGFromMainGame();
            } else if (this._shouldPlayBonusBG) {
                SoundManager.playFreeSpinBG();
            }
        } else {
            SoundManager.playAmbientLoop(HorribleHeroesSoundConfig.instance.MAIN_GAME_AMBIANCE_1);
        }
    }
}